import type { LayerGains } from "../../audio/engine";
import type { FeatureLayerGains } from "../../audio/featureEngine";
import type { SonificationMode } from "../../domain/mode";
import { FaderRow, percent } from "./FaderRow";

interface Props {
  sonificationMode: SonificationMode;
  onSonificationModeChange: (mode: SonificationMode) => void;
  /** All-epochs view: the feature engine is unavailable and the classic faders show. */
  stackedView: boolean;
  /** Counts of detected features in the current spectrum, when the feature engine is live. */
  featureCounts: { known: number; discovery: number } | null;
  masterGain: number;
  onMasterGainChange: (value: number) => void;
  layerGains: LayerGains;
  onLayerGainChange: (layer: keyof LayerGains, value: number) => void;
  featureLayerGains: FeatureLayerGains;
  onFeatureLayerGainChange: (layer: keyof FeatureLayerGains, value: number) => void;
}

const FEATURE_FADERS: Array<[keyof FeatureLayerGains, string]> = [
  ["lines", "Known lines"],
  ["discovery", "Discovery"],
  ["continuum", "Continuum pad"],
  ["events", "Sweep events"],
];

const CLASSIC_FADERS: Array<[keyof LayerGains, string]> = [
  ["continuum", "Continuum"],
  ["balmer", "Balmer voices"],
  ["oiii", "[O III] anchor"],
  ["dust", "Dust filter"],
];

/** Engine choice, master fader, and the layer faders of whichever engine is audible. */
export function EngineSection(props: Props) {
  const {
    sonificationMode,
    onSonificationModeChange,
    stackedView,
    featureCounts,
    masterGain,
    onMasterGainChange,
    layerGains,
    onLayerGainChange,
    featureLayerGains,
    onFeatureLayerGainChange,
  } = props;
  const featureFaders = sonificationMode === "feature" && !stackedView;
  return (
    <>
      <div className="panel-section">
        <h2>Sonification</h2>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            className={sonificationMode === "classic" ? "active" : ""}
            onClick={() => onSonificationModeChange("classic")}
          >
            Classic drone
          </button>
          <button
            className={sonificationMode === "feature" ? "active" : ""}
            onClick={() => onSonificationModeChange("feature")}
            disabled={stackedView}
            title={
              stackedView
                ? "All-epochs mode uses the classic stacked engine"
                : "Voices derived from peaks detected in the displayed spectrum"
            }
          >
            Feature voices
          </button>
        </div>
        {sonificationMode === "feature" && featureCounts ? (
          <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
            {featureCounts.known} known line{featureCounts.known === 1 ? "" : "s"},{" "}
            {featureCounts.discovery} unidentified peak
            {featureCounts.discovery === 1 ? "" : "s"} detected. Pitch = wavelength
            (blue high), shimmer = line width, pan mirrors the plot.
          </p>
        ) : null}
      </div>

      <FaderRow label="Master" value={masterGain} onChange={onMasterGainChange} readout={percent(masterGain)} />
      {featureFaders
        ? FEATURE_FADERS.map(([key, label]) => (
            <FaderRow
              key={key}
              label={label}
              value={featureLayerGains[key]}
              onChange={(v) => onFeatureLayerGainChange(key, v)}
              readout={percent(featureLayerGains[key])}
            />
          ))
        : CLASSIC_FADERS.map(([key, label]) => (
            <FaderRow
              key={key}
              label={label}
              value={layerGains[key]}
              onChange={(v) => onLayerGainChange(key, v)}
              readout={percent(layerGains[key])}
            />
          ))}
    </>
  );
}
